import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { connect } from 'react-redux';
import { Dispatch } from 'redux';
import dayjs from 'dayjs';
import styled from 'styled-components';
import { confirmAlert } from 'react-confirm-alert';
import 'react-confirm-alert/src/react-confirm-alert.css';

import { State } from 'src/state/ducks';
import * as NotificationTemplateActions from 'src/state/ducks/notificationTemplate/actions';
import { NotificationTemplateEntity } from 'src/firebase/db/NotificationTemplates';

import StyleConfig from 'src/constants/StyleConfig';
import { DefaultPageProps, HistoryProps } from 'src/constants/Interface';
import { formatDateToDate, copyTimeOnlyHHmmss } from 'src/helpers/UtilityHelper';

import { DefaultLayout } from 'src/components/layouts/DefaultLayout';
import Heading from 'src/components/atoms/typo/Heading';
import HomeButton from 'src/components/atoms/button/HomeButton';
import Button, { LinkButton } from 'src/components/atoms/button/Button';
import CheckBox from 'src/components/atoms/form/CheckBox';
import Tooltip from 'src/components/atoms/tooltip/Tooltip';
import SpinnerModal from 'src/components/organisms/loading/SpinnerModal';
import NotificationTemplateList from 'src/components/organisms/list/NotificationTemplateList';
import ConfirmModal from 'src/components/organisms/modal/ConfirmModal';

type OwnProps = {
  id: string;
  notification_template_ids: string;
};

type Props = DefaultPageProps &
  HistoryProps &
  OwnProps &
  ReturnType<typeof mapStateToProps> &
  ReturnType<typeof mapDispatchToProps>;

type CheckedTemplateProps = {
  id: string;
  checked: boolean;
};

const NotificationTemplateIndexPage = (props: Props) => {
  const [isCheckedTemplates, setIsCheckedTemplates] = useState<CheckedTemplateProps[]>([]);
  const [isCheckedAllTemplates, setIsCheckedAllTemplates] = useState<boolean>(false);
  const {
    login_shop_id,
    notification_template_ids,
    getNotificationTemplates,
    deleteNotificationTemplate,
    isFetching,
    notificationTemplates,
    history,
  } = props;

  // 選択されたテンプレート
  const selecedNotificationTemplateIds = useMemo(
    () => isCheckedTemplates.filter((v) => v.checked).map((v) => v.id),
    [isCheckedTemplates],
  );

  // GETパラメータから取得した初期選択対象のテンプレートID
  const initialIds = useMemo(
    () => (notification_template_ids ? notification_template_ids.split(',') : []),
    [notification_template_ids],
  );

  // 対象時間が1分前を過ぎているか
  const isExpiredTime = useCallback((timestamp?: Date) => {
    const time = copyTimeOnlyHHmmss(formatDateToDate(timestamp));
    const deadline = dayjs(new Date()).add(1, 'minute').toDate();
    return dayjs(time).isBefore(deadline);
  }, []);

  // 選択可能なデータが存在しない
  const isAllDisabled = useMemo(
    () =>
      notificationTemplates.filter(({ scheduled_time }) => !isExpiredTime(scheduled_time))
        .length === 0,
    [notificationTemplates, isExpiredTime],
  );

  // 初期表示：URIから取得した日付内の通知を取得
  useEffect(() => {
    getNotificationTemplates(login_shop_id);
  }, [getNotificationTemplates, login_shop_id]);

  // テンプレート一括配信用の初期値をセット
  useEffect(() => {
    //各テンプレートのチェック状態
    const initialValues = notificationTemplates.map((v) => ({
      id: v.id,
      checked: initialIds.includes(v.id),
    }));
    setIsCheckedTemplates(initialValues);

    //「全てのテンプレート選択する」の初期値
    const selectableTemplates = notificationTemplates.filter(
      ({ scheduled_time }) => !isExpiredTime(scheduled_time),
    );
    if (initialIds.length > 0 && initialIds.length === selectableTemplates.length) {
      setIsCheckedAllTemplates(true);
    }
  }, [
    notificationTemplates,
    setIsCheckedTemplates,
    setIsCheckedAllTemplates,
    initialIds,
    isExpiredTime,
  ]);

  // 削除完了モーダル
  const onCompleteDelete = () => {
    confirmAlert({
      customUI: ({ onClose }) => (
        <ConfirmModal
          title="テンプレート削除が完了しました"
          contentText="テンプレートを削除しました。"
          onClose={onClose}
          closeText="とじる"
        />
      ),
    });
  };

  // 削除
  const onDelete = useCallback(
    async (id: string) => {
      confirmAlert({
        customUI: ({ onClose }) => (
          <ConfirmModal
            title="テンプレート削除確認画面"
            contentText="テンプレートを削除しますか？"
            closeText="もどる"
            onClose={onClose}
            submitText="削除する"
            onSubmit={async () => {
              deleteNotificationTemplate(id);
              onClose();
              onCompleteDelete();
            }}
          />
        ),
      });
    },
    [deleteNotificationTemplate],
  );

  // 確認画面へ（単一テンプレート）
  const onConfirm = useCallback(
    (notificationTemplateId: string) => {
      history.push(`/notifications/create/?notification_template_id=${notificationTemplateId}`);
    },
    [history],
  );

  // 確認画面へ（複数テンプレート）
  const onMultipleConfirm = useCallback(() => {
    history.push(
      `/notifications/multiple_confirm/?notification_template_ids=${selecedNotificationTemplateIds.join(
        ',',
      )}`,
    );
  }, [history, selecedNotificationTemplateIds]);

  // テンプレート編集画面へ
  const onEdit = useCallback(
    (notificationTemplateId: string) => {
      history.push(`/notification_templates/${notificationTemplateId}/edit`);
    },
    [history],
  );

  // テンプレートのチェック状態切り替え
  const onCheck = useCallback(
    (notificationTemplateId: string) => {
      const values = isCheckedTemplates.map((v) => ({
        ...v,
        checked: v.id === notificationTemplateId ? !v.checked : v.checked,
      }));
      setIsCheckedTemplates(values);
    },
    [isCheckedTemplates, setIsCheckedTemplates],
  );

  // テンプレートのチェック状態を一度に全部切り替え
  const onCheckAll = useCallback(() => {
    const checked = !isCheckedAllTemplates;
    const values = isCheckedTemplates.map((v) => {
      const template = notificationTemplates.find(
        (t) => t.id === v.id,
      ) as NotificationTemplateEntity;
      const { scheduled_time } = template;
      let bool = false;
      // 時間外のテンプレートは選択不可
      if (isExpiredTime(scheduled_time)) {
        bool = false;
      } else {
        bool = v.checked === checked ? v.checked : checked;
      }
      return {
        ...v,
        checked: bool,
      };
    });
    setIsCheckedTemplates(values);
    setIsCheckedAllTemplates(checked);
  }, [
    isCheckedTemplates,
    setIsCheckedTemplates,
    isCheckedAllTemplates,
    setIsCheckedAllTemplates,
    isExpiredTime,
    notificationTemplates,
  ]);

  return (
    <DefaultLayout {...props}>
      <SpinnerModal isLoading={isFetching} />
      <Container>
        <div className="p-NotificationTemplateIndexPage">
          <Heading tag="h1" text={`テンプレートからパンの焼き上がりをお知らせします`} />

          <p className="lead">
            お知らせをテンプレートから選択し、配信頂けます。
            また、「通知を配信する」ボタンから、テンプレートを利用して新たに通知を配信することができます。
          </p>
          <CheckAll>
            <div data-tip={isAllDisabled ? '選択可能なテンプレートが存在しません。' : null}>
              <CheckBox
                isDisabled={isAllDisabled}
                children={'全てのテンプレートを選択する'}
                isChecked={isCheckedAllTemplates}
                onChange={() => onCheckAll()}
              />
              <Tooltip type="error" />
            </div>
          </CheckAll>

          <NotificationTemplateList
            notificationTemplates={notificationTemplates}
            isCheckedTemplates={isCheckedTemplates}
            isExpiredTime={isExpiredTime}
            onConfirm={onConfirm}
            onDelete={onDelete}
            onEdit={onEdit}
            onCheck={onCheck}
          />
          <ButtonWrapper>
            <Button
              isDisabled={!selecedNotificationTemplateIds.length}
              clickEvent={() => {
                onMultipleConfirm();
              }}
              text="選択したテンプレートをまとめて配信する"
            />
          </ButtonWrapper>
          <ButtonWrapper>
            <LinkButton
              isDisabled={false}
              url="/notification_templates/create"
              text="テンプレートを新規登録"
            />
          </ButtonWrapper>
          <div className="item">
            <HomeButton />
          </div>
        </div>
      </Container>
    </DefaultLayout>
  );
};

const Container = styled.div`
  box-sizing: border-box;
  padding: ${StyleConfig.HELPER.M20};
  .p-NotificationTemplateIndexPage {
    > .lead {
      margin-bottom: ${StyleConfig.HELPER.M20};
    }
    > .item {
      margin: ${StyleConfig.HELPER.M24} auto;
    }
  }
`;

const CheckAll = styled.div`
  margin-bottom: ${StyleConfig.HELPER.M20};
  > * {
    display: inline-flex;
  }
  input[type='checkbox'] {
    border: 1px solid ${StyleConfig.COLOR.INPUT_BORDER} !important;
    &:disabled {
      background-color: ${StyleConfig.COLOR.INPUT_BORDER};
      cursor: not-allowed;
    }
  }
`;

const ButtonWrapper = styled.div`
  margin: 24px 0;
`;

const mapStateToProps = (state: State) => {
  return {
    notificationTemplates: state.notificationTemplate.resource,
    isFetching: state.notification.filterByDate.isFetching,
    error: state.notificationTemplate.apiError,
  };
};

const mapDispatchToProps = (dispatch: Dispatch) => ({
  getNotificationTemplates: (shopId: string) =>
    dispatch(
      NotificationTemplateActions.getNotificationTemplates({
        shopId,
        templateIds: [],
      }),
    ),
  deleteNotificationTemplate: (notificationTemplateId: string) =>
    dispatch(NotificationTemplateActions.deleteNotificationTemplate(notificationTemplateId)),
});

export default connect(mapStateToProps, mapDispatchToProps)(NotificationTemplateIndexPage);
